import React, { useState } from "react";
import { X, RefreshCw, Send, CheckCircle } from "lucide-react";
import "./HeroSection.css";
import heroDevBg from "../assets/assets/hero-developers-bg.jpg";
import CaptchaBadge from "../components/CaptchaBadge";

const HeroSection = () => {
  const [showInlineForm, setShowInlineForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [captchaCode, setCaptchaCode] = useState("PROD88");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    captcha: "",
  });

  const handleGetInTouchClick = (e) => {
    e.preventDefault();
    const contactSection =
      document.getElementById("contact-support") ||
      document.querySelector(".contact-support-section-v2");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRefreshCaptcha = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "";
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaCode(code);
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setShowInlineForm(false);
      setSubmitted(false);
      setFormData({ name: "", email: "", phone: "", message: "", captcha: "" });
    }, 2800);
  };

  const ArrowIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M6 6v2h8.59L5 17.59L6.41 19L16 9.41V18h2V6z"></path>
    </svg>
  );

  return (
    <section className="hero">
      {/* Background Artwork Layer */}
      <div className="hero-img1-bg-layer">
        {/* Right Side Organic Photo Frame with Coding Background */}
        <div className="hero-organic-frame">
          <img src={heroDevBg} alt="Developer Coding" className="hero-organic-img" />
          <div className="hero-dark-blend-overlay"></div>
        </div>

        {/* Bottom Left Purple Fluid Blob Shape */}
        <div className="hero-bottomleft-blob">
          <div className="scroll-down-indicator">
            <span className="scroll-text">Scroll Down</span>
            <div className="scroll-arrow-line"></div>
          </div>
        </div>
      </div>

      <div className="container-full-bottom">
        <div className="hero-content-left">
          <div className="flex-v">
            <h1 className="h1">
              Empower Your Business with Custom AI Solutions
            </h1>
            <div className="body-l-light max-width-500-to-450">
              Accelerate operations, reduce costs, and unlock new growth with AI
              strategies tailored for your organization.
            </div>
          </div>

          <div className="hero-buttons-wrap">
            <button
              className="btn btn-primary hero-get-in-touch-btn"
              onClick={handleGetInTouchClick}
            >
              <span>Get in Touch</span>
              <span className="btn-icon">
                <ArrowIcon />
              </span>
            </button>
          </div>

          {/* Inline Contact Form directly inside Hero Section (Image 2) */}
          {showInlineForm && (
            <div className="hero-inline-form-card">
              <button
                type="button"
                className="hero-inline-close-btn"
                onClick={() => setShowInlineForm(false)}
              >
                <X size={18} />
              </button>

              {submitted ? (
                <div className="hero-inline-success">
                  <CheckCircle size={44} className="success-icon" />
                  <h3>Message Sent!</h3>
                  <p>Thank you for contacting us. We will respond within 10 minutes.</p>
                </div>
              ) : (
                <form className="hero-inline-form" onSubmit={handleSubmitForm}>
                  <div className="hero-inline-header">
                    <h3>Get in Touch</h3>
                    <p>Fill out the form below to connect with our team.</p>
                  </div>

                  <div className="hero-inline-field">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="hero-inline-field-grid">
                    <div className="hero-inline-field">
                      <label>Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="name@company.com"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="hero-inline-field">
                      <label>Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="+1 (555) 000-0000"
                        value={formData.phone}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="hero-inline-field">
                    <label>Message / Project Details *</label>
                    <textarea
                      name="message"
                      rows="3"
                      required
                      placeholder="How can we help your business?"
                      value={formData.message}
                      onChange={handleInputChange}
                    ></textarea>
                  </div>

                  {/* Captcha Verification */}
                  <div className="hero-inline-captcha-row">
                    <div className="captcha-box">
                      <CaptchaBadge
                        code={captchaCode}
                        width={90}
                        height={34}
                        bgColor="#ffffff"
                        textColor="#0f172a"
                        fontSize={17}
                      />
                      <button
                        type="button"
                        className="captcha-refresh-btn"
                        onClick={handleRefreshCaptcha}
                      >
                        <RefreshCw size={14} />
                      </button>
                    </div>
                    <input
                      type="text"
                      name="captcha"
                      required
                      placeholder="Enter captcha"
                      className="captcha-input"
                      value={formData.captcha}
                      onChange={handleInputChange}
                      onPaste={(e) => e.preventDefault()}
                      autoComplete="off"
                    />
                  </div>

                  <button type="submit" className="hero-inline-submit-btn">
                    <span>Send Message</span>
                    <Send size={16} />
                  </button>
                </form>
              )}
            </div>
          )}
        </div>

        {/* Right Side Metric Cards Grid (Matching User Screenshot Layout) */}
        <div className="hero-content-right">
          <div className="hero-metrics-top">
            <div className="hero-metric-empty"></div>
            <div className="hero-metric hero-metric-gradient1 hero-metric-top-right">
              <div className="hero-metric-number">99%</div>
              <div className="hero-metric-title">Client Satisfaction Rate</div>
              <div className="body-m-light1">
                Delivering reliable solutions that consistently exceed client expectations.
              </div>
            </div>
          </div>
          <div className="hero-metrics-bottom">
            <div className="hero-metric hero-metric-bottom-left">
              <div className="hero-metric-number">3X</div>
              <div className="hero-metric-title">Faster Decision-Making</div>
              <div className="body-m-light">
                Enables quick access to real-time information for faster, informed decisions
              </div>
            </div>
            <div className="hero-metric hero-metric-gradient hero-metric-bottom-right">
              <div className="hero-metric-number">40%</div>
              <div className="hero-metric-title">Cost Reduction</div>
              <div className="body-m-light">
                Drive sustainable savings through automation and process optimization.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
